import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import IngredientInputs from "./IngredientInputs";
import DirectionInputs from "./DirectionInputs";
import {
  DndContext,
  MeasuringStrategy,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  verticalListSortingStrategy,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

/* 
  RecipeForm component that is used to either create a new recipe or edit an existing one
 
  Parameters:
    initValues: An object containing initial values for the recipe form.
                If empty, the form is for creating a new recipe.
                If populated, it is for editing an existing recipe.
  */
const RecipeForm = ({ initValues = {} }) => {
  console.log("Init: ", initValues);
  const isNewRecipe = Object.keys(initValues).length === 0;
  let navigate = useNavigate();

  // Set the form data to either initValues or nothing
  const [formData, setFormData] = useState(initValues || {});
  useEffect(() => {
    setFormData(initValues || {});
  }, [initValues]);
  // const handleFormDataChange = (e) => {
  //   setFormData({ ...formData, [e.target.name]: e.target.value });
  // };

  const [metaState, setMetaState] = useState({
    title: isNewRecipe ? "" : JSON.parse(initValues.title),
    description: isNewRecipe ? "" : JSON.parse(initValues.body),
  });
  const handleMetaChange = (e) =>
    setMetaState({
      ...metaState,
      [e.target.name]: [e.target.value],
    });

  const blankIngredient = { amount: "", units: "", name: "" };
  const [ingredientState, setIngredientState] = useState(
    isNewRecipe ? [{ ...blankIngredient }] : JSON.parse(initValues.ingredients)
  );
  const addIngredient = () => {
    setIngredientState([...ingredientState, { ...blankIngredient }]);
  };
  const handleIngredientChange = (e) => {
    const updatedIngredients = [...ingredientState];
    updatedIngredients[e.target.dataset.idx][e.target.className] =
      e.target.value;
    setIngredientState(updatedIngredients);
  };

  const blankDirection = { content: "" };
  const [directionState, setDirectionState] = useState(
    isNewRecipe ? [{ ...blankDirection }] : JSON.parse(initValues.directions)
  );
  const addDirection = () => {
    setDirectionState([...directionState, { ...blankDirection }]);
  };
  const handleDirectionChange = (e) => {
    const updatedDirections = [...directionState];
    updatedDirections[e.target.dataset.idx][e.target.className] =
      e.target.value;
    setDirectionState(updatedDirections);
  };

  // let [recipe, setRecipe] = useState(null);
  let createRecipe = async () => {
    await fetch(`/api/recipes/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        updated: new Date(),
        title: JSON.stringify(metaState.title),
        body: JSON.stringify(metaState.description),
        ingredients: JSON.stringify(ingredientState),
        directions: JSON.stringify(directionState),
      }),
    });
  };

  let updateRecipe = async () => {
    await fetch(`/api/recipes/${initValues.id}/`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        updated: new Date(),
        title: JSON.stringify(metaState.title),
        body: JSON.stringify(metaState.description),
        ingredients: JSON.stringify(ingredientState),
        directions: JSON.stringify(directionState),
      }),
    });
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    console.log("Drag End: ", active, over);
    if (active.id !== over.id) {
      setIngredientState((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  let handleSubmit = (e) => {
    e.preventDefault();
    if (Object.keys(initValues).length === 0) {
      createRecipe();
    } else {
      updateRecipe();
    }

    navigate("/");
  };

  return (
    <form onSubmit={handleSubmit}>
      <label htmlFor="title">Title</label>
      <input
        type="text"
        name="title"
        id="title"
        value={metaState.title}
        onChange={handleMetaChange}
      />
      <label htmlFor="description">Description</label>
      <textarea
        name="description"
        id="description"
        value={metaState.description}
        onChange={handleMetaChange}
      ></textarea>
      <hr></hr>
      <h3>Ingredient List</h3>

      {ingredientState.map((val, idx) => (
        <IngredientInputs
          key={`ingredient-${idx}`}
          idx={idx}
          ingredientState={ingredientState}
          handleIngredientChange={handleIngredientChange}
        />
      ))}
      <input type="button" value="Add Ingredient +" onClick={addIngredient} />
      <hr></hr>
      <h3>Instructions List</h3>
      <DndContext>
        {directionState.map((val, idx) => (
          <DirectionInputs
            key={`direction-${idx}`}
            idx={idx}
            directionState={directionState}
            handleDirectionChange={handleDirectionChange}
          />
        ))}
      </DndContext>
      <input type="button" value="Add Instruction +" onClick={addDirection} />
      <button type="submit" value="Submit" name="button">
        {isNewRecipe ? "Create Recipe" : "Update Recipe"}
      </button>
    </form>
  );
};

export default RecipeForm;
